import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AlbumGrid from "@/components/AlbumGrid";
import { ALBUMS, HERO_PHOTO } from "@/data/photos";
import { motion } from "framer-motion";
import { Award, Heart, Globe } from "lucide-react";

export default function Fondatrice() {
  const album = ALBUMS.find((a) => a.id === "portraits")!;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      {/* @section: fondatrice-hero */}
      <section className="relative min-h-[70vh] flex items-end pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_PHOTO} alt="Mme Belhuguette Beko Bokau" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[oklch(0.15_0.05_240/0.95)] via-[oklch(0.2_0.07_240/0.6)] to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.1_0.04_240/0.7)] to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="inline-block text-accent text-sm font-semibold uppercase tracking-[0.15em] mb-3">La Fondatrice</span>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Mme Belhuguette<br /><span className="text-accent">Beko Bokau</span>
            </h1>
            <p className="text-white/70 text-lg max-w-xl">Entrepreneuse, femme d'affaires et fondatrice de la FGNB, engagée pour le développement humain et la solidarité.</p>
          </motion.div>
        </div>
      </section>

      {/* Bio */}
      {/* @section: fondatrice-bio */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 mb-20">
            {[
              { icon: Heart, title: "Sa vision", text: "Construire un monde où les talents sont révélés et où les personnes les plus vulnérables retrouvent espoir, autonomie et dignité." },
              { icon: Award, title: "Son engagement", text: "Fondatrice de la FGNB, elle œuvre sans relâche pour transformer des vies à travers des actions humanitaires concrètes et durables." },
              { icon: Globe, title: "Son impact", text: "Des milliers de vies touchées à travers des programmes d'éducation, de santé, d'entrepreneuriat et de promotion des talents." },
            ].map(({ icon: Icon, title, text }) => (
              <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                className="bg-card border border-border rounded-3xl p-7 hover:border-accent/30 hover:shadow-[0_8px_32px_oklch(0.72_0.11_45/0.1)] transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-primary/8 border border-primary/15 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>

          {/* Quote */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="rounded-3xl p-10 text-center mb-20 relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, oklch(0.28 0.09 240) 0%, oklch(0.2 0.07 240) 100%)" }}>
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-accent/5 blur-3xl" />
            <p className="text-4xl text-accent font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>"</p>
            <p className="text-xl text-white/90 italic max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Ensemble, révélons les talents, redonnons espoir et construisons un avenir meilleur.
            </p>
            <p className="mt-4 text-accent/80 text-sm font-medium">— Mme Belhuguette Beko Bokau, Fondatrice FGNB</p>
          </motion.div>

          {/* Album portraits */}
          {/* @section: fondatrice-album */}
          <div className="mb-6">
            <span className="inline-block text-accent text-sm font-semibold uppercase tracking-[0.15em] mb-2">Album photos</span>
            <h2 className="text-3xl font-bold text-foreground mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {album.title}
            </h2>
          </div>
          <AlbumGrid photos={album.photos} columns={3} />
        </div>
      </section>

      <Footer />
    </div>
  );
}
